import Fuse from 'fuse.js';

// Cache for canonical products data
let canonicalProductsCache = null;
let canonicalProductsVersion = null;

// Cache busting version parameter - uses timestamp for development, version hash for production
const getCacheBustingParam = () => {
  if (process.env.NODE_ENV === 'production') {
    // In production, we'll use the version from the JSON file
    return `v=${canonicalProductsVersion || Date.now()}`;
  }
  // In development, always use timestamp to ensure fresh data
  return `v=${Date.now()}`;
};

// Prepare canonical products on load
let loadPromise = null;

// Get the CDN URL at runtime — works in both dev and embedded production contexts
function getCdnUrl() {
  // Prefer the explicit CDN_URL set by loader.js on the window config
  if (typeof window !== 'undefined' && window.STORE_LOCATOR_CONFIG?.CDN_URL) {
    return window.STORE_LOCATOR_CONFIG.CDN_URL.replace(/\/$/, '');
  }
  // Hard-coded fallback — always correct for this project
  return 'https://ddeewr0txvw1f.cloudfront.net';
}

// Load canonical products from JSON with cache busting
export async function loadCanonicalProducts() {
  // Return cached promise if already loading
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    try {
      // Always use the absolute CDN URL as the primary source.
      // import.meta.env.BASE_URL bakes in '/' at build time and is wrong
      // when the widget is embedded on an external site.
      const cdnUrl = getCdnUrl();
      const cacheBustParam = getCacheBustingParam();
      const url = `${cdnUrl}/canonical-products.json?${cacheBustParam}`;

      console.debug('Attempting to load canonical products from:', url);
      let response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to load canonical products: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Only update cache if we got a newer version
      if (!canonicalProductsVersion || data.version !== canonicalProductsVersion) {
        canonicalProductsCache = data.products;
        canonicalProductsVersion = data.version;
        if (process.env.NODE_ENV === 'development') {         
          console.log(`Loaded canonical products version ${data.version}`);
        }
      }
      
      return canonicalProductsCache;
    } catch (error) {
      console.error('Failed to load canonical products:', error);
      // Reset so a future call can retry rather than returning the failed promise
      loadPromise = null;
      // Return cached data if available from a previous successful load
      return canonicalProductsCache || [];
    }
  })();
  
  return loadPromise;
}

// Initialize the fuzzy search
let fuse = null;

async function initializeFuse() {
  if (!fuse) {
    const products = await loadCanonicalProducts();
    fuse = new Fuse(products, {
      keys: ['skus', 'aliases', 'priceCategory'],
      threshold: 0.25,
    });
  }
  return fuse;
}

// Price-based categorization logic
function categorizePriceRange(price) {
  if (price <= 25) return 'pocket';
  if (price > 25 && price < 45) return 'standard';
  if (price > 55) return 'premium';
  return 'ambiguous'; // 45-60 range, needs fuzzy matching
}

// Get all products for a specific flavor group
async function getProductsByFlavorGroup(flavorGroup) {
  const products = await loadCanonicalProducts();
  return products.filter(product => product.flavorGroup === flavorGroup);
}

// Parse product name into structured components
function parseProductName(name) {
  const lower = name.toLowerCase();
  const mgMatch = lower.match(/(\d+)\s*mg/);
  const dosage = mgMatch ? parseInt(mgMatch[1], 10) : null;
  let line = null;
  if (/pocket/.test(lower)) line = 'pocket';
  else if (/\b500\b/.test(lower)) line = 'standard';
  else if (/\b1000|\b1k\b/.test(lower)) line = 'premium';
  else if (/plus/.test(lower)) line = 'plus';
  else if (/\bx\b/.test(lower)) line = 'x';
  return { flavor: null, dosage, line };
}

// Enhanced product matching with price-based categorization
export async function getCanonicalProductInfo(productName, price = null) {
  // Structured lookup by parsed components
  const { dosage, line } = parseProductName(productName);
  const products = await loadCanonicalProducts();
  // Derive flavor from exact group matching in products
  const flavor = products.find(p =>
    productName.toLowerCase().includes(p.flavorGroup.toLowerCase())
  )?.flavorGroup;
  if (flavor) {
    let candidates = products.filter(p => p.flavorGroup === flavor && p.priceCategory !== 'legacy');
    if (line) candidates = candidates.filter(p => p.priceCategory === line);
    if (dosage) {
      const cat = categorizePriceRange(dosage);
      candidates = candidates.filter(p => p.priceCategory === cat);
    }
    if (candidates.length === 1) return candidates[0];
  }

  // Fallback to existing fuzzy logic
  const searchFuse = await initializeFuse();
  const results = searchFuse.search(productName);
  
  // If no matches, return null
  if (results.length === 0) {
    return null;
  }

  // If no price provided, return the best fuzzy match
  if (price === null || typeof price !== 'number') {
    return results[0].item;
  }

  // Exclude legacy products when price is provided
  const filteredResults = results.filter(r => r.item.priceCategory !== 'legacy');
  const candidateResults = filteredResults.length > 0 ? filteredResults : results;
  // Map results to items
  const items = candidateResults.map(r => r.item);
  // Determine desired price category
  const priceCategory = categorizePriceRange(price);
  // If ambiguous, return best match
  if (priceCategory === 'ambiguous') {
    return items[0];
  }
  // Try find an item matching the price category
  const match = items.find(item => item.priceCategory === priceCategory);
  // Return matched item or fallback to best match
  return match || items[0];
}

// Helper function to get all possible matches for a product name with their price categories
export async function getAllPossibleMatches(productName) {
  const searchFuse = await initializeFuse();
  const results = searchFuse.search(productName);
  
  if (results.length === 0) {
    return [];
  }

  const bestMatch = results[0].item;
  const flavorGroup = bestMatch.flavorGroup;
  
  // Get all products in the same flavor group
  const flavorGroupProducts = await getProductsByFlavorGroup(flavorGroup);
  
  return flavorGroupProducts.map(product => ({
    ...product,
    fuzzyScore: results.find(r => r.item.canonicalName === product.canonicalName)?.score || 1
  })).sort((a, b) => a.fuzzyScore - b.fuzzyScore);
}

// Preload canonical products on module import for better performance
loadCanonicalProducts().catch(console.error);

export default {
  getCanonicalProductInfo,
  getAllPossibleMatches,
  loadCanonicalProducts
};
